/**
 * What a step did, in words. Every row in a reply's activity is one sentence about one tool
 * call, written for the state it is in: "Reading README.md…" while it runs, "Read README.md"
 * once done, "Couldn't read README.md" when it failed, "Waiting to stop a session" while it
 * waits for a person. Claude Code's own tools (Read, Bash, Grep, WebSearch, Task, ...) and the
 * orgus capabilities (titles such as "Stop a session") go through one verb table, so every
 * tense comes from the plain verb. Pure, so it is tested (__tests__/labels.test.ts).
 */
import type { AssistantStep } from '@orgus/protocol';
import { formatCount } from '@orgus/ui';
import { TOOL_PREFIX, isToolCapability } from './model.ts';

/** The kind of work a tool does: it picks the icon, the tint and the header clause. */
export type ToolFamily = 'read' | 'write' | 'search' | 'web' | 'shell' | 'agent' | 'plan' | 'skill' | 'orgus-read' | 'orgus-change' | 'other';

export type Phase = 'running' | 'done' | 'failed' | 'waiting' | 'refused';

/** A sentence in parts: "Stopped" + "a session". */
export interface Action {
  /** The plain verb, lower case: "stop", "look for". */
  plain: string;
  /** What it acted on, as written in the sentence ("" for none). */
  object: string;
}

const ORGUS = 'mcp__orgus__';

/** The tool as Claude Code names it: 'Bash', 'TodoWrite', 'mcp__orgus__session_stop'. */
export function toolName(step: Pick<AssistantStep, 'tool' | 'capability'>): string {
  if (step.tool) return step.tool;
  if (isToolCapability(step.capability)) return step.capability.slice(TOOL_PREFIX.length);
  return `${ORGUS}${step.capability.replace(/\./g, '_')}`;
}

export const isOrgusTool = (name: string): boolean => name.startsWith(ORGUS);

const READ_TOOLS = new Set(['Read', 'NotebookRead', 'BashOutput']);
const WRITE_TOOLS = new Set(['Write', 'Edit', 'MultiEdit', 'NotebookEdit']);
const SEARCH_TOOLS = new Set(['Grep', 'Glob', 'LS']);
const SHELL_TOOLS = new Set(['Bash', 'PowerShell', 'KillShell']);

export function toolFamily(step: Pick<AssistantStep, 'tool' | 'capability' | 'tier'>): ToolFamily {
  const name = toolName(step);
  if (isOrgusTool(name)) return step.tier === 'read' ? 'orgus-read' : 'orgus-change';
  if (READ_TOOLS.has(name)) return 'read';
  if (WRITE_TOOLS.has(name)) return 'write';
  if (SEARCH_TOOLS.has(name)) return 'search';
  if (name === 'WebSearch' || name === 'WebFetch') return 'web';
  if (SHELL_TOOLS.has(name)) return 'shell';
  if (name === 'Task' || name === 'Agent') return 'agent';
  if (name === 'TodoWrite') return 'plan';
  if (name === 'Skill') return 'skill';
  return 'other';
}

// ---------------------------------------------------------------------------
// Verbs
// ---------------------------------------------------------------------------

const PAST: Record<string, string> = {
  add: 'added',
  analyse: 'analysed',
  analyze: 'analyzed',
  approve: 'approved',
  ask: 'asked',
  build: 'built',
  call: 'called',
  cancel: 'cancelled',
  change: 'changed',
  check: 'checked',
  clean: 'cleaned',
  clear: 'cleared',
  clone: 'cloned',
  commit: 'committed',
  compare: 'compared',
  compile: 'compiled',
  copy: 'copied',
  count: 'counted',
  create: 'created',
  delete: 'deleted',
  deny: 'denied',
  download: 'downloaded',
  edit: 'edited',
  fetch: 'fetched',
  find: 'found',
  fix: 'fixed',
  format: 'formatted',
  get: 'got',
  inspect: 'inspected',
  install: 'installed',
  kill: 'killed',
  launch: 'launched',
  lint: 'linted',
  list: 'listed',
  load: 'loaded',
  look: 'looked',
  make: 'made',
  merge: 'merged',
  move: 'moved',
  open: 'opened',
  pause: 'paused',
  plan: 'planned',
  print: 'printed',
  pull: 'pulled',
  push: 'pushed',
  query: 'queried',
  read: 'read',
  remove: 'removed',
  rename: 'renamed',
  restart: 'restarted',
  resume: 'resumed',
  review: 'reviewed',
  run: 'ran',
  save: 'saved',
  search: 'searched',
  send: 'sent',
  set: 'set',
  show: 'showed',
  start: 'started',
  stop: 'stopped',
  summarise: 'summarised',
  summarize: 'summarized',
  switch: 'switched',
  test: 'tested',
  try: 'tried',
  typecheck: 'typechecked',
  update: 'updated',
  use: 'used',
  verify: 'verified',
  view: 'viewed',
  wait: 'waited',
  watch: 'watched',
  write: 'wrote',
};

const DOUBLED: Record<string, string> = { run: 'running', stop: 'stopping', get: 'getting', set: 'setting', plan: 'planning', commit: 'committing', begin: 'beginning', debug: 'debugging' };

/** The -ing form: run → running, write → writing, see → seeing. */
export function gerund(plain: string): string {
  const [verb, ...rest] = plain.split(' ');
  const v = verb!;
  const ing = DOUBLED[v] ?? (/[^e]e$/.test(v) ? `${v.slice(0, -1)}ing` : /ie$/.test(v) ? `${v.slice(0, -2)}ying` : `${v}ing`);
  return [ing, ...rest].join(' ');
}

/** The past form: stop → stopped, look for → looked for. Unknown verbs keep their plain form. */
export function past(plain: string): string {
  const [verb, ...rest] = plain.split(' ');
  return [PAST[verb!] ?? verb!, ...rest].join(' ');
}

const cap = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** The sentence for an action in one phase. */
export function sentence(a: Action, phase: Phase): string {
  const obj = a.object ? ` ${a.object}` : '';
  switch (phase) {
    case 'running':
      return `${cap(gerund(a.plain))}${obj}`;
    case 'done':
      return `${cap(past(a.plain))}${obj}`;
    case 'failed':
      return `Couldn't ${a.plain}${obj}`;
    case 'waiting':
      return `Waiting to ${a.plain}${obj}`;
    case 'refused':
      return `Didn't ${a.plain}${obj}`;
  }
}

// ---------------------------------------------------------------------------
// Objects
// ---------------------------------------------------------------------------

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** The last part of a path: C:\repo\src\a.ts → a.ts. */
export function baseName(p: string): string {
  const parts = p.replace(/[\\/]+$/, '').split(/[\\/]/);
  return parts[parts.length - 1] || p;
}

export function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url.slice(0, 60);
  }
}

/** Curly quotes around a phrase, clipped at a word. */
export function quoted(s: string, max = 48): string {
  const t = s.replace(/\s+/g, ' ').replace(/^["“”']+|["“”']+$/g, '').trim();
  if (t.length <= max) return `“${t}”`;
  const cut = t.slice(0, max);
  const sp = cut.lastIndexOf(' ');
  return `“${(sp > max / 2 ? cut.slice(0, sp) : cut).trimEnd()}…”`;
}

/** A glob in words: **\/*.ts → TS files; src/**  → files in src; a literal → its name. */
export function globWords(pattern: string): string {
  const ext = /\*\.([A-Za-z0-9]{1,8})$/.exec(pattern);
  if (ext && !/[{[]/.test(pattern)) return `${ext[1]!.toUpperCase()} files`;
  if (!/[*?[{]/.test(pattern)) return baseName(pattern);
  return `files matching ${quoted(pattern, 40)}`;
}

/** A shell command, short: its first line, clipped. */
function commandWords(cmd: string): string {
  const first = cmd.split('\n')[0]!.trim();
  return first.length > 56 ? `${first.slice(0, 55).trimEnd()}…` : first;
}

/** Bash's description in Claude Code's words ("List files in src") as an action, or null. */
function describedAction(description: string): Action | null {
  const m = /^([A-Za-z]+)\s+(.*)$/.exec(description.trim().replace(/\.$/, ''));
  if (!m) return null;
  const verb = m[1]!.toLowerCase();
  if (!PAST[verb]) return null;
  return { plain: verb, object: m[2]!.charAt(0).toLowerCase() + m[2]!.slice(1) };
}

/** The todos a TodoWrite call sent. */
export interface Todo {
  content: string;
  status: 'pending' | 'in_progress' | 'completed';
  activeForm?: string;
}

export function todosOf(input: Readonly<Record<string, unknown>>): Todo[] {
  const list = Array.isArray(input.todos) ? input.todos : [];
  return list
    .filter((t): t is Record<string, unknown> => !!t && typeof t === 'object')
    .map((t) => ({
      content: str(t.content) || 'Untitled step',
      status: t.status === 'completed' || t.status === 'in_progress' ? t.status : 'pending',
      ...(str(t.activeForm) ? { activeForm: str(t.activeForm) } : {}),
    }));
}

/** What a TodoWrite call did to the plan, as a done sentence. */
export function planSentence(todos: readonly Todo[]): string {
  const n = todos.length;
  if (!n) return 'Cleared its plan';
  const done = todos.filter((t) => t.status === 'completed').length;
  const current = todos.findIndex((t) => t.status === 'in_progress');
  if (done === n) return n === 1 ? 'Finished the one step' : `Finished all ${n} steps`;
  if (current >= 0) return `Started step ${current + 1} of ${n}: ${todos[current]!.content}`;
  if (done === 0) return n === 1 ? 'Planned one step' : `Planned ${n} steps`;
  return `Finished ${done} of ${n} steps`;
}

/** orgus capabilities whose title reads badly as a sentence. */
const ORGUS_ACTIONS: Record<string, Action> = {
  'state.summary': { plain: 'check', object: 'the state of everything' },
  'needs_you.list': { plain: 'check', object: 'what needs you' },
  'session.get': { plain: 'read', object: 'a session' },
  'costs.summary': { plain: 'check', object: 'spend and budgets' },
  'project.remove': { plain: 'stop', object: 'watching a project' },
};

/** The capability's nouns for a counted list: sessions.list → "sessions". */
function listNoun(capability: string): string | null {
  const m = /^([a-z_]+)\.list$/.exec(capability);
  if (!m) return null;
  const noun = m[1]!.replace(/_/g, ' ');
  // audit.list, feed.list: not countable nouns; their titles read well.
  return noun.endsWith('s') ? noun : null;
}

/** How many things a list answer holds: an array, or { items | data | results | ... }. */
export function countOf(result: string | undefined): number | null {
  if (!result) return null;
  try {
    const v = JSON.parse(result) as unknown;
    if (Array.isArray(v)) return v.length;
    if (v && typeof v === 'object') {
      const o = v as Record<string, unknown>;
      // The real total wins over a list the hub shortened to fit.
      if (typeof o.total === 'number') return o.total;
      for (const k of ['items', 'data', 'results', 'sessions', 'machines', 'pipelines', 'projects', 'jobs', 'approvals', 'missions', 'entries', 'events', 'pending', 'list']) {
        const x = o[k];
        if (typeof o[`${k}Total`] === 'number') return o[`${k}Total`] as number;
        if (Array.isArray(x)) return x.length;
      }
    }
  } catch {
    return null;
  }
  return null;
}

/** The action a step stands for, from its tool and input (and, once done, its answer). */
export function actionOf(step: Pick<AssistantStep, 'tool' | 'capability' | 'title' | 'input' | 'result' | 'outcome'>): Action {
  const name = toolName(step);
  const i = step.input ?? {};
  switch (name) {
    case 'Read':
    case 'NotebookRead':
      return { plain: 'read', object: baseName(str(i.file_path) || str(i.notebook_path) || 'a file') };
    case 'Write':
      return { plain: 'write', object: baseName(str(i.file_path) || 'a file') };
    case 'Edit':
    case 'MultiEdit':
      return { plain: 'edit', object: baseName(str(i.file_path) || 'a file') };
    case 'NotebookEdit':
      return { plain: 'edit', object: baseName(str(i.notebook_path) || 'a notebook') };
    case 'Grep':
      return { plain: 'search', object: str(i.pattern) ? `files for ${quoted(str(i.pattern), 44)}` : 'files' };
    case 'Glob':
      return { plain: 'look for', object: str(i.pattern) ? globWords(str(i.pattern)) : 'files' };
    case 'LS':
      return { plain: 'list', object: str(i.path) ? baseName(str(i.path)) : 'a folder' };
    case 'Bash':
    case 'PowerShell': {
      const d = describedAction(str(i.description));
      if (d) return d;
      return { plain: 'run', object: str(i.command) ? `\`${commandWords(str(i.command))}\`` : 'a command' };
    }
    case 'BashOutput':
      return { plain: 'read', object: 'shell output' };
    case 'KillShell':
      return { plain: 'stop', object: 'a shell' };
    case 'WebSearch':
      return { plain: 'search', object: str(i.query) ? `the web for ${quoted(str(i.query), 56)}` : 'the web' };
    case 'WebFetch':
      return { plain: 'read', object: str(i.url) ? hostOf(str(i.url)) : 'a page' };
    case 'Task':
    case 'Agent': {
      const d = str(i.description);
      return { plain: 'ask', object: d ? `an agent to ${d.charAt(0).toLowerCase()}${d.slice(1)}` : 'an agent for help' };
    }
    case 'Skill':
      return { plain: 'use', object: str(i.skill) ? `the ${str(i.skill)} skill` : 'a skill' };
    case 'TodoWrite':
      return { plain: 'update', object: 'its plan' };
  }
  if (isOrgusTool(name)) {
    const capability = step.capability;
    if (capability === 'api.get') return { plain: 'read', object: str(i.path) || 'a module API' };
    if (capability === 'api.call') return { plain: 'call', object: [str(i.method).toUpperCase(), str(i.path)].filter(Boolean).join(' ') || 'a module API' };
    const fixed = ORGUS_ACTIONS[capability];
    if (fixed) return fixed;
    const noun = listNoun(capability);
    if (noun) {
      const n = step.outcome === 'ok' ? countOf(step.result) : null;
      if (n === 0) return { plain: 'find', object: `no ${noun}` };
      if (n !== null) return { plain: 'list', object: `${formatCount(n)} ${n === 1 ? noun.replace(/s$/, '') : noun}` };
      return { plain: 'list', object: noun };
    }
    const m = /^([A-Za-z]+)\s*(.*)$/.exec(step.title.trim());
    if (m && PAST[m[1]!.toLowerCase()]) return { plain: m[1]!.toLowerCase(), object: m[2]! };
    return { plain: 'use', object: step.title ? `“${step.title}”` : 'an Orgus tool' };
  }
  return { plain: 'use', object: humanise(name) };
}

/** A tool name people can read: mcp__jira__create_issue → Jira: create issue; NotebookRead → notebook read. */
export function humanise(name: string): string {
  const mcp = /^mcp__([^_]+(?:_[^_]+)*)__(.+)$/.exec(name);
  if (mcp) return `${cap(mcp[1]!.replace(/[_-]/g, ' '))}: ${mcp[2]!.replace(/[_-]+/g, ' ').toLowerCase()}`;
  return name
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase();
}

/** The phase a step's row is written in. */
export function phaseOf(step: Pick<AssistantStep, 'outcome'>): Phase {
  switch (step.outcome) {
    case 'running':
      return 'running';
    case 'pending':
      return 'waiting';
    case 'error':
      return 'failed';
    case 'denied':
    case 'expired':
      return 'refused';
    default:
      return 'done';
  }
}

/** The row's sentence for a step, as it stands now. */
export function stepSentence(step: Pick<AssistantStep, 'tool' | 'capability' | 'title' | 'input' | 'result' | 'outcome'>): string {
  const name = toolName(step);
  if (name === 'TodoWrite' && step.outcome !== 'error') {
    const todos = todosOf(step.input ?? {});
    return step.outcome === 'running' ? 'Updating its plan' : planSentence(todos);
  }
  return sentence(actionOf(step), phaseOf(step));
}

// ---------------------------------------------------------------------------
// Counting
// ---------------------------------------------------------------------------

/** A noun's plural: file → files, search → searches, query → queries. */
export function pluralNoun(noun: string): string {
  if (/(s|x|z|ch|sh)$/.test(noun)) return `${noun}es`;
  if (/[^aeiou]y$/.test(noun)) return `${noun.slice(0, -1)}ies`;
  return `${noun}s`;
}

/**
 * "Read a file" four times reads "Read 4 files"; "Stopped a session in app" twice reads
 * "Stopped 2 sessions in app". Null when the sentence does not name one thing with a or an.
 */
export function countedAction(label: string, n: number): string | null {
  const m = /^(\p{Lu}[\p{L}']*(?: (?:to|for))?) (?:a|an) (\p{Ll}[\p{L}-]*)(.*)$/u.exec(label);
  if (!m) return null;
  return `${m[1]} ${n} ${pluralNoun(m[2]!)}${m[3]}`;
}
